import React from 'react';
import { useEmail } from '../../context/EmailContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import PermissionGate from '../../common/PermissionGate';

const AutopilotSettings = () => {
  const { settings, updateSettings, queue } = useEmail();
  const { permissions } = useCRM();

  // If not enabled/entitled, show upsell or gate
  if (!permissions.canEmailAutopilot) {
    return (
      <div className="p-8 bg-slate-50 rounded-2xl border border-slate-200 text-center">
        <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon="🚀" className="w-8 h-8" />
        </div>
        <h3 className="text-lg font-black text-slate-900">Email Autopilot (Enterprise)</h3>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
          Automatiser utsending direkte fra CRM. Krever Enterprise-lisens og domeneverifisering.
        </p>
        <button className="mt-6 px-6 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold uppercase tracking-widest">
          Kontakt Salg
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Status Card */}
      <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
              <SafeIcon icon="🚀" className="text-purple-600" /> Autopilot Status
            </h3>
            <p className="text-sm text-slate-500 mt-1">Automatisk sending av e-postutkast.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={settings.autopilot_enabled}
              onChange={(e) => updateSettings({ autopilot_enabled: e.target.checked })}
              disabled={!settings.spf_verified || !settings.dkim_verified}
            />
            <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>

        {/* DNS Checks */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className={`p-4 rounded-xl border ${settings.spf_verified ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-red-50 border-red-100 text-red-700'}`}>
            <p className="text-xs font-black uppercase tracking-widest mb-1">SPF</p>
            <div className="flex items-center gap-2 font-bold text-sm">
              <SafeIcon icon={settings.spf_verified ? "✅" : "❌"} />
              {settings.spf_verified ? "Verifisert" : "Mangler"}
            </div>
          </div>
          <div className={`p-4 rounded-xl border ${settings.dkim_verified ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-red-50 border-red-100 text-red-700'}`}>
            <p className="text-xs font-black uppercase tracking-widest mb-1">DKIM</p>
            <div className="flex items-center gap-2 font-bold text-sm">
              <SafeIcon icon={settings.dkim_verified ? "✅" : "❌"} />
              {settings.dkim_verified ? "Verifisert" : "Mangler"}
            </div>
          </div>
          <div className="p-4 rounded-xl border bg-slate-50 border-slate-100 text-slate-700">
            <p className="text-xs font-black uppercase tracking-widest mb-1">DMARC</p>
            <div className="flex items-center gap-2 font-bold text-sm">
              <SafeIcon icon="ℹ️" />
              Anbefalt
            </div>
          </div>
        </div>

        {(!settings.spf_verified || !settings.dkim_verified) && (
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800 font-medium flex items-center gap-2">
            <SafeIcon icon="⚠️" /> Du må verifisere domenet ditt før du kan aktivere autopilot.
          </div>
        )}
      </div>

      {/* Queue & Limits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200">
          <h4 className="font-black text-slate-900 mb-4 text-sm uppercase tracking-widest">Rate Limits (Guardrails)</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Maks per bruker / dag</label>
              <input 
                type="number" 
                value={settings.daily_limit_per_user} 
                onChange={(e) => updateSettings({ daily_limit_per_user: parseInt(e.target.value) })}
                className="w-full p-2 border border-slate-200 rounded-lg text-sm font-bold"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Minimum forsinkelse (sekunder)</label>
              <input 
                type="number" 
                value={60} 
                disabled
                className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-400"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200">
          <h4 className="font-black text-slate-900 mb-4 text-sm uppercase tracking-widest">Aktiv Kø ({queue.length})</h4>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {queue.length > 0 ? queue.map(item => (
              <div key={item.queue_id} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg text-xs">
                <span className="font-mono text-slate-500">{item.queue_id.substring(0,8)}...</span>
                <span className={`px-2 py-0.5 rounded font-bold ${item.status === 'Sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  {item.status}
                </span>
              </div>
            )) : (
              <p className="text-slate-400 text-xs italic">Køen er tom.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutopilotSettings;